<template>
    <ValidationObserver
        ref="observer"
        v-slot="{ invalid }"
        tag="form"
        data-testid="cesion-form"
        class="tw:p-4 tw:rounded-lg tw:bg-slate-50 tw:border tw:border-slate-200 tw:space-y-4"
        @submit.prevent="onSubmit"
    >
        <h3 class="tw:font-semibold tw:text-slate-900">{{ $t('perfil.cesion_informacion_cesionario') }}</h3>

        <InputText
            v-model="formData.cesionario_nombre"
            :label="$t('perfil.cesion_nombre')"
            :name="$t('perfil.cesion_nombre')"
            rules="required"
            :disabled="loading"
            data-testid="cesion-form-nombre"
        />

        <InputText
            v-model="formData.cesionario_apellidos"
            :label="$t('perfil.cesion_apellidos')"
            :name="$t('perfil.cesion_apellidos')"
            rules="required"
            :disabled="loading"
            data-testid="cesion-form-apellidos"
        />

        <div data-testid="cesion-form-email">
            <BaseInputEmail
                v-model="formData.cesionario_email"
                :label="$t('perfil.cesion_email')"
                :name="$t('perfil.cesion_email')"
                rules="required"
                :disabled="loading"
            />
        </div>

        <div data-testid="cesion-form-telefono">
            <BaseInputPhone
                v-model="formData.cesionario_telefono"
                :label="$t('perfil.cesion_telefono')"
                :required="true"
                :disabled="loading"
                :forceValidate="submitted"
                @valid="phoneIsValid = $event"
            />
        </div>

        <div class="cesion-document-grid tw:grid tw:grid-cols-2 tw:gap-4">
            <DocumentType
                :value="formData.tipoDocumento"
                :index="0"
                :required="true"
                :disabled="loading"
                @input="formData.tipoDocumento = $event"
            />
            <DocumentValue
                v-model="formData.cesionario_dni"
                :type="formData.tipoDocumento"
                :index="0"
                :required="true"
                :disabled="loading"
                data-testid="cesion-form-dni"
            />
        </div>

        <p v-if="error" data-testid="cesion-form-error" class="tw:text-sm tw:text-red-600">{{ error }}</p>

        <div class="tw:flex tw:flex-col-reverse tw:gap-3 tw:border-t tw:border-slate-200 tw:pt-4 sm:tw:flex-row">
            <Button
                type="button"
                variant="default"
                :disabled="loading"
                custom-class="tw:w-full tw:flex-1 tw:font-semibold"
                @click="$emit('cancel')"
            >
                {{ $t('perfil.cesion_cancelar') }}
            </Button>
            <Button
                type="submit"
                variant="alt"
                :loading="loading"
                :disabled="invalid || !phoneIsValid"
                custom-class="tw:w-full tw:flex-1 tw:font-semibold"
                data-testid="cesion-form-submit"
            >
                {{ loading ? $t('perfil.cesion_cediendo') : $t('perfil.cesion_ceder_asiento') }}
            </Button>
        </div>

        <p class="tw:text-xs tw:text-slate-500">
            {{ $t('perfil.cesion_verificar_datos') }}
        </p>

        <input
            v-model="formData.cesionario_user_id"
            type="hidden"
        />
    </ValidationObserver>
</template>

<script setup>
    import { reactive, ref } from 'vue';
    import { ValidationObserver } from 'vee-validate';
    import BaseInputEmail from '@/modules/front/ui/BaseInputEmail.vue';
    import BaseInputPhone from '@/modules/front/ui/BaseInputPhone.vue';
    import Button from '@/modules/front/ui/Button.vue';
    import DocumentType from '@/modules/front/ui/DocumentType.vue';
    import DocumentValue from '@/modules/front/ui/DocumentValue.vue';
    import InputText from '@/modules/front/ui/InputText.vue';

    defineProps({
        loading: {
            type: Boolean,
            default: false
        },
        error: {
            type: String,
            default: null
        }
    });

    const emit = defineEmits(['submit', 'cancel']);

    const observer = ref(null);
    const submitted = ref(false);
    const phoneIsValid = ref(false);

    const formData = reactive({
        cesionario_nombre: '',
        cesionario_apellidos: '',
        cesionario_email: '',
        cesionario_telefono: '',
        tipoDocumento: 'DNI',
        cesionario_dni: '',
        cesionario_user_id: null
    });

    const onSubmit = async() => {
        submitted.value = true;

        const isValid = await observer.value.validate();
        if (!isValid) return;

        if (!phoneIsValid.value) return;

        emit('submit', {
            cesionario_nombre: formData.cesionario_nombre,
            cesionario_apellidos: formData.cesionario_apellidos,
            cesionario_email: formData.cesionario_email,
            cesionario_telefono: formData.cesionario_telefono,
            cesionario_dni: formData.cesionario_dni,
            cesionario_user_id: formData.cesionario_user_id
        });
    };
</script>
